import { collection, collectionGroup, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import type { TimesheetEntry, TimesheetEntryStatus } from '@gigdriver/domain';
import { callable } from './functions';
import { getFirestoreInstance } from './firestore';
import { fromFirestore } from './serde';

// Timesheet entries are written ONLY by Cloud Functions (see
// functions/src/timesheets.ts) — the Firestore rules deny client writes, so
// every mutation here is a callable. Dates cross the wire as ISO strings.

export interface TimesheetResult {
  entryId: string;
  status: TimesheetEntryStatus;
}

interface EntryRef {
  orgId: string;
  gigId: string;
  entryId: string;
}

const iso = (d: Date) => d.toISOString();

const clockInCall = callable<
  { orgId: string; gigId: string; positionId: string; bookingId: string; callId?: string },
  TimesheetResult
>('clockInToBooking');

const clockOutCall = callable<EntryRef & { breakMinutes?: number; notes?: string }, TimesheetResult>(
  'clockOutOfEntry',
);

const createCall = callable<
  {
    orgId: string;
    gigId: string;
    positionId: string;
    bookingId: string;
    callId?: string;
    clockIn: string;
    clockOut: string;
    breakMinutes?: number;
    notes?: string;
  },
  TimesheetResult
>('createTimesheetEntry');

const updateCall = callable<
  EntryRef & { clockIn?: string; clockOut?: string; breakMinutes?: number | null; notes?: string | null },
  TimesheetResult
>('updateTimesheetEntry');

const reviewCall = callable<EntryRef & { decision: 'APPROVED' | 'REJECTED'; note?: string }, TimesheetResult>(
  'reviewTimesheetEntry',
);

const reopenCall = callable<EntryRef, TimesheetResult>('reopenTimesheetEntry');
const deleteCall = callable<EntryRef, { entryId: string }>('deleteTimesheetEntry');

// --- person -----------------------------------------------------------------

export const clockIn = clockInCall;

export const clockOut = clockOutCall;

// --- reviewer (coordinator+) ------------------------------------------------

export function createTimesheetEntry(input: {
  orgId: string;
  gigId: string;
  positionId: string;
  bookingId: string;
  callId?: string;
  clockIn: Date;
  clockOut: Date;
  breakMinutes?: number;
  notes?: string;
}) {
  return createCall({ ...input, clockIn: iso(input.clockIn), clockOut: iso(input.clockOut) });
}

export function updateTimesheetEntry(
  input: EntryRef & {
    clockIn?: Date;
    clockOut?: Date;
    breakMinutes?: number | null;
    notes?: string | null;
  },
) {
  return updateCall({
    ...input,
    clockIn: input.clockIn && iso(input.clockIn),
    clockOut: input.clockOut && iso(input.clockOut),
  });
}

export function reviewTimesheetEntry(input: EntryRef & { decision: 'APPROVED' | 'REJECTED'; note?: string }) {
  return reviewCall(input);
}

// manager+ only
export const reopenTimesheetEntry = reopenCall;
export const deleteTimesheetEntry = deleteCall;

// --- subscriptions ----------------------------------------------------------

type Handlers = {
  onData: (entries: TimesheetEntry[]) => void;
  onError?: (err: unknown) => void;
};

function parse(docs: Array<{ data: () => unknown }>): TimesheetEntry[] {
  return docs.map(d => fromFirestore<TimesheetEntry>(d.data()));
}

// Every entry on one gig, newest first (coordinator+; powers the gig's
// Timesheets section).
export function watchGigTimesheetEntries(orgId: string, gigId: string, h: Handlers): () => void {
  const q = query(
    collection(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'timesheetEntries'),
    orderBy('clockIn', 'desc'),
  );
  return onSnapshot(q, snap => h.onData(parse(snap.docs)), err => h.onError?.(err));
}

// The signed-in person's own recent entries across every gig/org (Time
// Clock page). Filtered on userId — the only shape the collection-group
// rule permits for a non-reviewer.
export function watchMyTimesheetEntries(userId: string, h: Handlers, max = 100): () => void {
  const q = query(
    collectionGroup(getFirestoreInstance(), 'timesheetEntries'),
    where('userId', '==', userId),
    orderBy('clockIn', 'desc'),
    limit(max),
  );
  return onSnapshot(q, snap => h.onData(parse(snap.docs)), err => h.onError?.(err));
}

// Org-wide review queue (coordinator+): entries awaiting review, plus OPEN
// ones so the UI can flag a forgotten clock-out (see needsAttention).
export function watchReviewQueue(orgId: string, h: Handlers): () => void {
  const q = query(
    collectionGroup(getFirestoreInstance(), 'timesheetEntries'),
    where('orgId', '==', orgId),
    where('status', 'in', ['OPEN', 'PENDING']),
    orderBy('clockIn', 'desc'),
  );
  return onSnapshot(q, snap => h.onData(parse(snap.docs)), err => h.onError?.(err));
}
